import { useState, useEffect, useCallback } from 'react';
import CustomerApiService, { type FavoritesResponse, type Favorite } from '@/services/CustomerApiService';

interface UseCustomerFavoritesParams {
  perPage?: number;
  productType?: string;
  page?: number;
}

interface UseCustomerFavoritesReturn {
  favorites: Favorite[];
  isLoading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    total: number;
    perPage: number;
    lastPage: number;
  } | null;
  refetch: () => Promise<void>;
  removeFavorite: (favoriteId: number) => Promise<void>;
}

export function useCustomerFavorites(params?: UseCustomerFavoritesParams): UseCustomerFavoritesReturn {
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<UseCustomerFavoritesReturn['pagination']>(null);

  const fetchFavorites = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await CustomerApiService.getFavorites(params);
      setFavorites(data.data || []);
      setPagination({
        currentPage: data.current_page || 1,
        total: data.total || 0,
        perPage: data.per_page || 10,
        lastPage: data.last_page || 1,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch favorites';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [params?.perPage, params?.productType, params?.page]);

  const removeFavorite = useCallback(async (favoriteId: number) => {
    try {
      await CustomerApiService.removeFavorite(favoriteId);
      setFavorites(prev => prev.filter(fav => fav.id !== favoriteId));
    } catch (err) {
      // Remove error - rethrow to be handled by caller
      throw err;
    }
  }, []);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  return {
    favorites,
    isLoading,
    error,
    pagination,
    refetch: fetchFavorites,
    removeFavorite,
  };
}

